import React, { useState, useEffect } from 'react';
import billingService from '../../api/billingService';

const SettingsPage = () => {
  const [activeTab, setActiveTab] = useState('basic');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    basic: {
      companyName: '',
      gstNumber: '',
      phone: '',
      email: ''
    },
    communication: {
      enabled: false,
      templates: []
    }
  });

  // Template Editor State
  const [showEditor, setShowEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editorTab, setEditorTab] = useState('config'); // config, headers, params, body

  const variables = [
    { label: 'Customer Name', value: '{{customer_name}}' },
    { label: 'Customer Phone', value: '{{customer_phone}}' },
    { label: 'Customer Email', value: '{{customer_email}}' },
    { label: 'Company Name', value: '{{company_name}}' },
    { label: 'Customer ID', value: '{{customer_id}}' },
    { label: 'Group ID', value: '{{group_id}}' },
    { label: 'Service Alias', value: '{{service_alias}}' },
    { label: 'Product Name', value: '{{product_name}}' },
    { label: 'Amount', value: '{{amount}}' },
    { label: 'Quantity', value: '{{qty}}' },
    { label: 'Renewal Date', value: '{{renewal_date}}' },
    { label: 'Start Date', value: '{{start_date}}' },
    { label: 'Service Detail', value: '{{service_detail}}' }
  ];

  useEffect(() => {
    fetchSettings();
  }, []);

  const getTemplateKey = (template) => String(template?._id || template?.id || '');

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await billingService.getSettings();
      if (res.success) setSettings(res.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (settingsToSave = settings) => {
    try {
      setSaving(true);
      const res = await billingService.updateSettings(settingsToSave);
      if (res.success) {
        await fetchSettings();
        alert('Settings updated successfully!');
        return true;
      }
    } catch (error) {
      alert(error.message);
    } finally {
      setSaving(false);
    }
    return false;
  };

  const openEditor = (template = null) => {
    if (template) {
      setEditingTemplate({
        ...template,
        headers: template.headers || [],
        queryParams: template.queryParams || [],
        bodyFields: template.bodyFields || [],
        bodyType: template.bodyType || 'json'
      });
    } else {
      setEditingTemplate({
        id: Date.now(),
        name: 'New Template',
        endpoint: '',
        method: 'POST',
        headers: [{ key: 'Content-Type', value: 'application/json' }],
        queryParams: [],
        bodyType: 'json',
        bodyJson: '{\n  "to": "{{customer_phone}}",\n  "message": "Hi {{customer_name}}, your service {{service_alias}} is expiring on {{renewal_date}}"\n}',
        bodyFields: []
      });
    }
    setShowEditor(true);
  };

  const saveTemplate = async () => {
    const editingKey = getTemplateKey(editingTemplate);
    const normalizedTemplate = editingKey
      ? editingTemplate
      : { ...editingTemplate, id: `tmp_${Date.now()}` };
    const normalizedKey = getTemplateKey(normalizedTemplate);
    const exists = settings.communication.templates.some(t => getTemplateKey(t) === normalizedKey);
    const finalTemplates = exists 
      ? settings.communication.templates.map(t => (
          getTemplateKey(t) === normalizedKey ? normalizedTemplate : t
        ))
      : [...settings.communication.templates, normalizedTemplate];

    const updatedSettings = {
      ...settings,
      communication: { ...settings.communication, templates: finalTemplates }
    };

    setSettings(updatedSettings);
    const saved = await handleSave(updatedSettings);
    if (saved) {
      setShowEditor(false);
    }
  };

  const removeTemplate = (templateOrId) => {
    const targetKey = typeof templateOrId === 'object'
      ? getTemplateKey(templateOrId)
      : String(templateOrId || '');
    if (window.confirm('Are you sure you want to remove this template?')) {
      setSettings({
        ...settings,
        communication: {
          ...settings.communication,
          templates: settings.communication.templates.filter(t => getTemplateKey(t) !== targetKey)
        }
      });
    }
  };

  const addField = (type) => {
    const field = { key: '', value: '' };
    setEditingTemplate({
      ...editingTemplate,
      [type]: [...editingTemplate[type], field]
    });
  };

  const updateField = (type, index, key, value) => {
    const updated = [...editingTemplate[type]];
    updated[index] = { ...updated[index], [key]: value };
    setEditingTemplate({ ...editingTemplate, [type]: updated });
  };

  const removeField = (type, index) => {
    setEditingTemplate({
      ...editingTemplate,
      [type]: editingTemplate[type].filter((_, i) => i !== index)
    });
  };

  const copyToClipboard = (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(() => {
        alert(`Copied: ${text}`);
      }).catch(err => {
        fallbackCopy(text);
      });
    } else {
      fallbackCopy(text);
    }
  };

  const fallbackCopy = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.left = "-9999px";
    textArea.style.top = "0";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
      alert(`Copied: ${text}`);
    } catch (err) {
      console.error('Fallback copy failed', err);
    }
    document.body.removeChild(textArea);
  };

  const filteredTemplates = settings.communication.templates.filter(t =>
    (t.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (t.endpoint || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <div className="p-5 text-center">Loading settings...</div>;

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-700 mb-1">Settings</h4>
          <p className="text-muted small mb-0">Configure your business and communication preferences.</p>
        </div>
        <button className="btn btn-primary px-4" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="card-header bg-white border-0 pt-3 px-4">
          <div className="d-flex justify-content-between align-items-center">
            <ul className="nav nav-pills card-header-pills">
              <li className="nav-item">
                <button className={`nav-link ${activeTab === 'basic' ? 'active' : 'text-muted'}`} onClick={() => setActiveTab('basic')}>Basic Settings</button>
              </li>
              <li className="nav-item">
                <button className={`nav-link ${activeTab === 'communication' ? 'active' : 'text-muted'}`} onClick={() => setActiveTab('communication')}>Communication</button>
              </li>
            </ul>
            <div className="input-group w-25">
              <span className="input-group-text bg-light border-0"><i className="bi bi-search"></i></span>
              <input 
                type="text" 
                className="form-control bg-light border-0 small" 
                placeholder="Search settings..." 
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="card-body p-4">
          {activeTab === 'basic' && (
            <div className="row g-4" style={{ maxWidth: '800px' }}>
              <div className="col-md-6">
                <label className="form-label text-muted small fw-600">COMPANY NAME</label>
                <input 
                  type="text" 
                  className="form-control" 
                  value={settings.basic.companyName}
                  onChange={e => setSettings({...settings, basic: {...settings.basic, companyName: e.target.value}})}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label text-muted small fw-600">BUSINESS GST NUMBER</label>
                <input 
                  type="text" 
                  className="form-control" 
                  value={settings.basic.gstNumber}
                  onChange={e => setSettings({...settings, basic: {...settings.basic, gstNumber: e.target.value}})}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label text-muted small fw-600">PHONE NUMBER</label>
                <input 
                  type="text" 
                  className="form-control" 
                  value={settings.basic.phone}
                  onChange={e => setSettings({...settings, basic: {...settings.basic, phone: e.target.value}})}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label text-muted small fw-600">EMAIL ADDRESS</label>
                <input 
                  type="email" 
                  className="form-control" 
                  value={settings.basic.email}
                  onChange={e => setSettings({...settings, basic: {...settings.basic, email: e.target.value}})}
                />
              </div>
            </div>
          )}

          {activeTab === 'communication' && (
            <div>
              <div className="form-check form-switch mb-4">
                <input 
                  className="form-check-input" 
                  type="checkbox" 
                  id="enableComm"
                  checked={settings.communication.enabled}
                  onChange={e => setSettings({...settings, communication: {...settings.communication, enabled: e.target.checked}})}
                />
                <label className="form-check-label fw-600" htmlFor="enableComm">Enable API Communications in Follow-ups</label>
              </div>

              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-700 mb-0">API Templates</h6>
                <button className="btn btn-sm btn-outline-primary" onClick={() => openEditor()}>
                  <i className="bi bi-plus-lg me-1"></i> Create Template
                </button>
              </div>

              <div className="row g-3">
                {filteredTemplates.length === 0 ? (
                  <div className="col-12 text-center py-5 text-muted bg-light rounded border-dashed">
                    No templates found. Click "Create Template" to get started.
                  </div>
                ) : (
                  filteredTemplates.map(template => (
                    <div key={getTemplateKey(template) || template.name} className="col-md-6">
                      <div className="card border shadow-none hover-shadow transition">
                        <div className="card-body p-3">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="fw-700 mb-0">{template.name}</h6>
                            <span className={`badge badge-soft-${template.method === 'POST' ? 'success' : 'primary'} text-xs`}>{template.method}</span>
                          </div>
                          <div className="text-xs text-muted text-truncate mb-3">{template.endpoint || 'No endpoint set'}</div>
                          <div className="d-flex gap-2">
                            <button className="btn btn-sm btn-light flex-grow-1" onClick={() => openEditor(template)}>
                              <i className="bi bi-pencil me-1"></i> Edit
                            </button>
                            <button className="btn btn-sm btn-light text-danger" onClick={() => removeTemplate(template)}>
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Full Screen Template Editor */}
      {showEditor && editingTemplate && (
        <div className="modal d-block p-0" style={{ backgroundColor: 'white', zIndex: 1060 }}>
          <div className="modal-dialog modal-fullscreen m-0">
            <div className="modal-content border-0 h-100">
              <div className="modal-header border-bottom px-4 py-3 sticky-top bg-white">
                <div className="d-flex align-items-center">
                  <button className="btn btn-light btn-sm rounded-circle me-3" onClick={() => setShowEditor(false)}>
                    <i className="bi bi-arrow-left"></i>
                  </button>
                  <h5 className="modal-title fw-800">Configure Template</h5>
                </div>
                <div className="d-flex gap-2">
                  <button type="button" className="btn btn-light px-4" onClick={() => setShowEditor(false)}>Discard</button>
                  <button type="button" className="btn btn-primary px-4" onClick={saveTemplate}>Save Template</button>
                </div>
              </div>
              
              <div className="modal-body p-0 overflow-hidden d-flex h-100">
                {/* Left Sidebar: Variables */}
                <div className="border-end bg-light p-4" style={{ width: '280px', overflowY: 'auto' }}>
                  <h6 className="fw-700 mb-3 small text-muted uppercase">Variables</h6>
                  <p className="text-xs text-muted mb-4">Click to copy and paste into any field below.</p>
                  <div className="d-flex flex-column gap-2">
                    {variables.map(v => (
                      <div 
                        key={v.value} 
                        className="p-2 bg-white border rounded cursor-pointer hover-bg-light transition d-flex justify-content-between align-items-center"
                        onClick={() => copyToClipboard(v.value)}
                      >
                        <span className="text-xs fw-600">{v.label}</span>
                        <code className="text-primary" style={{ fontSize: '10px' }}>{v.value}</code>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-5 p-3 bg-white border rounded shadow-sm">
                    <h6 className="text-xs fw-800 mb-2">Pro Tip</h6>
                    <p className="text-xs text-muted mb-0">Use these variables in URL, Headers, or Body. They will be replaced automatically when sending.</p>
                  </div>
                </div>

                {/* Main Content Area */}
                <div className="flex-grow-1 d-flex flex-column" style={{ overflowY: 'auto' }}>
                  {/* Top Bar: Basic Config */}
                  <div className="p-4 border-bottom">
                    <div className="row g-4">
                      <div className="col-md-6">
                        <label className="form-label text-muted small fw-600">TEMPLATE NAME</label>
                        <input 
                          type="text" 
                          className="form-control form-control-lg fw-700" 
                          value={editingTemplate.name}
                          onChange={e => setEditingTemplate({...editingTemplate, name: e.target.value})}
                          placeholder="e.g. WhatsApp Expiry Reminder"
                        />
                      </div>
                      <div className="col-md-2">
                        <label className="form-label text-muted small fw-600">METHOD</label>
                        <select 
                          className="form-select form-select-lg" 
                          value={editingTemplate.method}
                          onChange={e => setEditingTemplate({...editingTemplate, method: e.target.value})}
                        >
                          <option value="GET">GET</option>
                          <option value="POST">POST</option>
                          <option value="PUT">PUT</option>
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label text-muted small fw-600">ENDPOINT URL</label>
                        <input 
                          type="text" 
                          className="form-control form-control-lg" 
                          value={editingTemplate.endpoint}
                          onChange={e => setEditingTemplate({...editingTemplate, endpoint: e.target.value})}
                          placeholder="https://api.provider.com/send"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Tabs: Headers, Params, Body */}
                  <div className="p-4">
                    <ul className="nav nav-tabs mb-4">
                      <li className="nav-item">
                        <button className={`nav-link fw-600 ${editorTab === 'headers' ? 'active' : ''}`} onClick={() => setEditorTab('headers')}>Headers</button>
                      </li>
                      <li className="nav-item">
                        <button className={`nav-link fw-600 ${editorTab === 'params' ? 'active' : ''}`} onClick={() => setEditorTab('params')}>Query Parameters</button>
                      </li>
                      <li className="nav-item">
                        <button className={`nav-link fw-600 ${editorTab === 'body' ? 'active' : ''}`} onClick={() => setEditorTab('body')}>Request Body</button>
                      </li>
                    </ul>

                    {/* Headers Editor */}
                    {editorTab === 'headers' && (
                      <div className="animation-fade-in">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h6 className="fw-700 mb-0">HTTP Headers</h6>
                          <button className="btn btn-sm btn-primary" onClick={() => addField('headers')}>+ Add Header</button>
                        </div>
                        {editingTemplate.headers.length === 0 && <div className="text-center py-4 text-muted border rounded bg-light">No headers added.</div>}
                        {editingTemplate.headers.map((h, i) => (
                          <div key={i} className="row g-2 mb-2 align-items-start">
                            <div className="col-4">
                              <input className="form-control form-control-sm" placeholder="Key (e.g. Authorization)" value={h.key} onChange={e => updateField('headers', i, 'key', e.target.value)} />
                            </div>
                            <div className="col-7">
                              <textarea className="form-control form-control-sm" rows="2" placeholder="Value (e.g. Bearer token)" value={h.value} onChange={e => updateField('headers', i, 'value', e.target.value)}></textarea>
                            </div>
                            <div className="col-1">
                              <button className="btn btn-sm btn-outline-danger w-100" onClick={() => removeField('headers', i)}><i className="bi bi-x"></i></button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Query Params Editor */}
                    {editorTab === 'params' && (
                      <div className="animation-fade-in">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h6 className="fw-700 mb-0">Query Parameters</h6>
                          <button className="btn btn-sm btn-primary" onClick={() => addField('queryParams')}>+ Add Parameter</button>
                        </div>
                        {editingTemplate.queryParams.length === 0 && <div className="text-center py-4 text-muted border rounded bg-light">No query parameters added.</div>}
                        {editingTemplate.queryParams.map((p, i) => (
                          <div key={i} className="row g-2 mb-2 align-items-start">
                            <div className="col-4">
                              <input className="form-control form-control-sm" placeholder="Key" value={p.key} onChange={e => updateField('queryParams', i, 'key', e.target.value)} />
                            </div>
                            <div className="col-7">
                              <textarea className="form-control form-control-sm" rows="2" placeholder="Value" value={p.value} onChange={e => updateField('queryParams', i, 'value', e.target.value)}></textarea>
                            </div>
                            <div className="col-1">
                              <button className="btn btn-sm btn-outline-danger w-100" onClick={() => removeField('queryParams', i)}><i className="bi bi-x"></i></button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Body Editor */}
                    {editorTab === 'body' && (
                      <div className="animation-fade-in">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <div className="d-flex align-items-center">
                            <h6 className="fw-700 mb-0 me-3">Request Body</h6>
                            <div className="btn-group btn-group-sm">
                              <button className={`btn ${editingTemplate.bodyType === 'json' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setEditingTemplate({...editingTemplate, bodyType: 'json'})}>Raw JSON</button>
                              <button className={`btn ${editingTemplate.bodyType === 'form' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setEditingTemplate({...editingTemplate, bodyType: 'form'})}>Form UI</button>
                            </div>
                          </div>
                          {editingTemplate.bodyType === 'form' && <button className="btn btn-sm btn-primary" onClick={() => addField('bodyFields')}>+ Add Field</button>}
                        </div>

                        {editingTemplate.bodyType === 'json' ? (
                          <textarea 
                            className="form-control font-monospace text-xs" 
                            rows="15" 
                            value={editingTemplate.bodyJson}
                            onChange={e => setEditingTemplate({...editingTemplate, bodyJson: e.target.value})}
                            placeholder='{"key": "value"}'
                          ></textarea>
                        ) : (
                          <div>
                            {editingTemplate.bodyFields.length === 0 && <div className="text-center py-4 text-muted border rounded bg-light">No fields added.</div>}
                            {editingTemplate.bodyFields.map((f, i) => (
                              <div key={i} className="row g-2 mb-2 align-items-start">
                                <div className="col-4">
                                  <input className="form-control form-control-sm" placeholder="Field Name" value={f.key} onChange={e => updateField('bodyFields', i, 'key', e.target.value)} />
                                </div>
                                <div className="col-7">
                                  <textarea className="form-control form-control-sm" rows="2" placeholder="Value" value={f.value} onChange={e => updateField('bodyFields', i, 'value', e.target.value)}></textarea>
                                </div>
                                <div className="col-1">
                                  <button className="btn btn-sm btn-outline-danger w-100" onClick={() => removeField('bodyFields', i)}><i className="bi bi-x"></i></button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;
